import { Flex } from '@components/Layout/Blocks';
import { Link } from '@components/Link';
import { useReactRouter } from '@hooks/useRouter';
import { useToggle } from '@hooks/useToggle';
import { useUser } from '@hooks/useUser';
import { t } from '@lib/translation';
import Collapse from '@material-ui/core/Collapse';
import Icon from '@material-ui/core/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@material-ui/icons/KeyboardArrowUpOutlined';
import { useApp } from '@packages/hooks/useApp';
import { Context } from '@packages/layout/lib/UIShell/context';
import { createMemo } from '@packages/react';
import { RouterServiceProvider } from '@packages/router/lib/RouterServiceProvider';
import { useCallback, useContext, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';

interface SidebarItemProps {
    label: string;
    href: string;
    badge?: any;
    icon?: any;
    exact?: boolean;
    inAccordion?: boolean;
    children?: any;
    hrefProps?: { [key: string]: any };
    isActive?: any;
}

const useStyles = makeStyles(() => ({
    sidebar: ({ isCustomer }: any) => ({
        marginTop: '8px',
        height: '40px',
        width: isCustomer ? '254px' : '100%',
        marginLeft: isCustomer ? '10px' : '0',
        paddingLeft: isCustomer ? '16px' : '24px',

        '&:hover': {
            background: '#FEE1E1',
            borderRadius: isCustomer ? '8px' : '0',
        },
    }),
    selectedSidebar: ({ isCustomer, inAccordion }: any) => ({
        background: '#FEE1E1',
        marginTop: '10px',
        height: '40px',
        borderRadius: isCustomer ? '8px' : '0',
        borderLeft: isCustomer || inAccordion ? 'none' : '4px solid #EA0A2A',
        width: isCustomer ? '254px' : '100%',
        marginLeft: isCustomer ? '12px' : '0',
        paddingLeft: isCustomer ? '14px' : '20px',
    }),
    label: {
        '& .MuiTypography-subtitle1': {
            fontWeight: 400,
            fontSize: '14px',
        },
    },
    activeLabel: {
        '& .MuiTypography-subtitle1': {
            fontWeight: 500,
            fontSize: '14px',
            color: '#EA0A2A',
        },
    },
}));

export const SidebarItem = createMemo<SidebarItemProps>(
    (props) => {
        const { label, icon, href, children, hrefProps, inAccordion, isActive, exact = true } = props;
        const location = useLocation();
        const user$ = useUser();
        const isCustomer = user$.user_view === 'customer';
        const { history } = useReactRouter();
        const router = useApp(RouterServiceProvider);
        const [state, { toggleDrawer }] = useContext(Context);
        const classes = useStyles({ isCustomer, inAccordion });

        const href$ = useMemo(() => router.route(href, hrefProps), [href, hrefProps]);
        const hrefPath$ = useMemo(() => router.getRouteUrl(href) || href, [href]);
        const matchActive = useMemo(
            () => matchPath(location.pathname, hrefPath$),
            [location.pathname, hrefPath$, exact],
        );
        const showList = useToggle(!!matchActive);

        const icon$ = useMemo(
            () =>
                typeof icon === 'string' ? (
                    <Icon color={matchActive ? 'primary' : 'inherit'}>{icon}</Icon>
                ) : isActive && matchActive ? (
                    isActive
                ) : (
                    icon
                ),
            [icon, matchActive],
        );

        const handleClick = useCallback(() => {
            if (state.get('page').hideDrawer) {
                toggleDrawer();
            }
        }, [toggleDrawer, state.get('page').hideDrawer]);

        const handleDropdownToggle = useCallback(
            (e) => {
                showList.handleToggle(e);

                if (!showList.current && children?.length) {
                    history.push(children[0].props.href);
                }
            },
            [showList.current, children],
        );

        if (children) {
            return (
                <div style={{ backgroundColor: showList.current && '#F9F9F9' }}>
                    <ListItem
                        button
                        onClick={handleDropdownToggle}
                        className={matchActive ? classes.selectedSidebar : classes.sidebar}
                        style={{ background: 'none' }}
                    >
                        <ListItemIcon>{icon$}</ListItemIcon>

                        <ListItemText
                            primary={label}
                            primaryTypographyProps={{ variant: 'subtitle1', style: { fontSize: '14px' } }}
                        />
                        <StyledListItemIcon>
                            <Flex justify={'end'} w={'100%'} align={'center'}>
                                {showList.current ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
                            </Flex>
                        </StyledListItemIcon>
                    </ListItem>

                    <Collapse in={showList.current} unmountOnExit style={{ transformOrigin: '50% 0' }}>
                        <List>{children}</List>
                    </Collapse>
                </div>
            );
        }

        return (
            <ListItem
                button
                component={Link}
                onClick={handleClick}
                to={href$}
                className={matchActive ? classes.selectedSidebar : classes.sidebar}
            >
                {icon$ && <ListItemIcon>{icon$}</ListItemIcon>}
                <ListItemText
                    primary={t(label)}
                    primaryTypographyProps={{ variant: 'subtitle1', color: matchActive ? 'primary' : 'inherit' }}
                    className={matchActive ? classes.activeLabel : classes.label}
                />
            </ListItem>
        );
    },
    {
        displayName: 'SidebarItem',
        defaultProps: {
            hrefProps: {},
        },
    },
);

const StyledListItemIcon = styled(ListItemIcon)`
    & {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 10px 16px;
    }
`;
